<template>
  <div>
    <v-toolbar flat color="white" class="hidden-sm-and-down">
      <v-toolbar-title>Kategori Yönetimi</v-toolbar-title>
      <v-divider class="mx-3" inset vertical></v-divider>
      
      <v-select
        v-model="selectedTenantId"
        :items="tenantOptions"
        item-text="tenantName"
        item-value="tenantId"
        label="Tenant Seç"
        @change="onTenantChange"
        style="min-width: 150px; margin-right: 10px; margin-top: 20px;"
      ></v-select>
      <v-divider class="mx-2" inset vertical></v-divider>
      
      <v-text-field 
        v-model="search" 
        append-icon="search" 
        label="Ara" 
        single-line 
        hide-details
      ></v-text-field>
      <v-spacer></v-spacer>
      <v-btn 
        color="primary" 
        dark 
        @click="showAddDialog" 
        class="mb-2"
        :disabled="!selectedTenantId"
      >
        <v-icon left>add</v-icon>
        Yeni Kategori Ekle
      </v-btn>
    </v-toolbar>

    <div class="hidden-md-and-up mobile-toolbar">
      <v-card flat>
        <v-card-title class="pb-2">
          <span class="headline">Kategori Yönetimi</span>
          <v-spacer></v-spacer>
          <v-btn 
            icon 
            color="primary" 
            @click="showAddDialog"
            :disabled="!selectedTenantId"
          >
            <v-icon>add</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text class="pt-0">
          <v-select
            v-model="selectedTenantId"
            :items="tenantOptions"
            item-text="tenantName"
            item-value="tenantId"
            label="Tenant Seç"
            @change="onTenantChange"
            outlined
            dense
            class="mb-2"
          ></v-select>
          
          <v-text-field 
            v-model="search" 
            append-icon="search" 
            label="Ara" 
            outlined
            dense
            hide-details
            class="mb-2"
          ></v-text-field>
        </v-card-text>
      </v-card>
    </div>

    <v-alert
      v-if="!selectedTenantId"
      type="warning"
      class="ma-4"
      prominent
    >
      <h3>Tenant Seçimi Gerekli</h3>
      <p>Kategori işlemlerini gerçekleştirmek için lütfen yukarıdan bir Tenant seçiniz.</p>
    </v-alert>

    <div v-if="selectedTenantId">
      <v-card class="elevation-2 category-tree-container">
        <v-card-text class="pa-0">
          <v-treeview
            v-if="categoryTree.length > 0"
            :items="categoryTree"
            :search="search"
            item-key="id"
            item-text="name"
            item-children="children"
            hoverable
            open-on-click
            class="enhanced-tree"
          >
            <template v-slot:prepend="{ item, open }">
              <v-icon
                v-if="item.children && item.children.length > 0"
                :class="{ 'rotate-icon': open }"
              >
                {{ open ? 'folder_open' : 'folder' }}
              </v-icon>
              <v-icon v-else color="grey lighten-1">
                label
              </v-icon>
            </template>

              <template v-slot:label="{ item }">
                <div class="tree-item-container">
                  <!-- Üst satır: Kategori adı ve butonlar -->
                  <div class="item-row-top">
                    <span class="category-name">{{ item.name }}</span>
                    
                    <div class="tree-item-actions">
                      <v-btn 
                        icon 
                        small
                        @click.stop="editCategoryFromTree(item)" 
                        class="action-btn mr-2"
                      >
                        <v-icon small>edit</v-icon>
                      </v-btn>
                      <v-btn 
                        icon 
                        small
                        @click.stop="deleteCategoryFromTree(item)" 
                        class="action-btn"
                      >
                        <v-icon small>delete</v-icon>
                      </v-btn>
                    </div>
                  </div>

                  <!-- Alt satır: Durum, Key, Sıra, Açıklama -->
                  <div class="item-row-bottom">
                    <v-chip 
                      :color="item.isActive ? 'success' : 'error'" 
                      text-color="white" 
                      small
                      class="mr-2"
                    >
                      {{ item.isActive ? 'Aktif' : 'Pasif' }}
                    </v-chip>

                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-chip
                          small
                          class="mr-2"
                          color="blue lighten-4"
                          text-color="blue darken-2"
                          v-on="on"
                        >
                          <v-icon small left>vpn_key</v-icon>
                          {{ item.categoryKey }}
                        </v-chip>
                      </template>
                      <span>Kategori Anahtarı</span>
                    </v-tooltip>

                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-chip
                          small
                          class="mr-2"
                          color="purple lighten-4"
                          text-color="purple darken-2"
                          v-on="on"
                        >
                          <v-icon small left>sort</v-icon>
                          Sıra: {{ item.sortOrder }}
                        </v-chip>
                      </template>
                      <span>Sıra Numarası</span>
                    </v-tooltip>

                    <v-tooltip top v-if="item.description">
                      <template v-slot:activator="{ on }">
                        <v-chip
                          small
                          class="mr-2"
                          color="grey lighten-2"
                          text-color="grey darken-3"
                          v-on="on"
                        >
                          <v-icon small left>info</v-icon>
                          Açıklama
                        </v-chip>
                      </template>
                      <span>{{ item.description }}</span>
                    </v-tooltip>
                  </div>
                </div>
              </template>
          </v-treeview>
          
          <div v-else class="text-center pa-8">
            <div v-if="loading">
              <v-progress-circular
                indeterminate
                color="primary"
                size="50"
              ></v-progress-circular>
              <p class="mt-4 grey--text">Yükleniyor...</p>
            </div>
            <div v-else>
              <v-icon size="64" color="grey lighten-1">folder_open</v-icon>
              <p class="mt-4 grey--text text--darken-1">Kategori bulunamadı.</p>
              <v-btn color="primary" outlined small @click="showAddDialog" class="mt-2">
                <v-icon left>add</v-icon>
                İlk Kategoriyi Ekle
              </v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <v-dialog v-model="dialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>
          <span class="headline">{{ editedIndex === -1 ? 'Yeni Kategori' : 'Kategori Düzenle' }}</span>
        </v-card-title>
        <v-card-text>
          <v-container grid-list-md>
            <v-layout wrap>
              <v-flex xs12>
                <v-text-field 
                  v-model="editedItem.categoryKey" 
                  label="Kategori Anahtarı" 
                  required
                  :error-messages="keyErrors"
                  @input="sanitizeCategoryKey"
                  @blur="checkCategoryKey"
                  :disabled="editedIndex !== -1"
                  hint="Sadece harf, rakam, tire (-) ve alt çizgi (_) kullanılabilir"
                  persistent-hint
                ></v-text-field>
              </v-flex>
              <v-flex xs12>
                <v-text-field 
                  v-model="editedItem.name" 
                  label="Kategori Adı" 
                  required
                ></v-text-field>
              </v-flex>
              <v-flex xs12>
                <v-textarea 
                  v-model="editedItem.description" 
                  label="Açıklama"
                  rows="3"
                ></v-textarea>
              </v-flex>
              <v-flex xs12>
                <v-select
                  v-model="editedItem.parentKey"
                  :items="parentOptions"
                  item-text="name"
                  item-value="categoryKey"
                  label="Ana Kategori"
                  clearable
                  @change="onParentChange"
                ></v-select>
              </v-flex>
              <v-flex xs6>
                <v-text-field 
                  v-model="editedItem.sortOrder" 
                  label="Sıra No" 
                  type="number"
                  required
                ></v-text-field>
              </v-flex>
              <v-flex xs6>
                <v-switch
                  v-model="editedItem.isActive"
                  label="Aktif"
                ></v-switch>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" flat @click="close">İptal</v-btn>
          <v-btn 
            color="blue darken-1" 
            flat 
            @click="save" 
            :loading="saving"
            :disabled="!isFormValid"
          >
            Kaydet
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline">Kategori Sil</v-card-title>
        <v-card-text>
          <strong>{{ categoryToDelete && categoryToDelete.name }}</strong> kategorisini silmek istediğinizden emin misiniz?
          <br><br>
          <v-alert type="warning" value="true">
            Bu işlem geri alınamaz!
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" flat @click="deleteDialog = false">İptal</v-btn>
          <v-btn 
            color="red darken-1" 
            flat 
            @click="confirmDelete"
            :loading="deleting"
          >
            Sil
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarText }}
      <v-btn dark flat @click="snackbar = false">Kapat</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  data() {
    return {
      selectedTenantId: null,
      tenantOptions: [
        { tenantId: 1, tenantName: "Altinda Firsat" },
        { tenantId: 2, tenantName: "EAltin" }
      ],
      search: '',
      loading: false,
      saving: false,
      deleting: false,
      dialog: false,
      deleteDialog: false,
      editedIndex: -1,
      keyErrors: [],
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      categoryToDelete: null,
      editedItem: {
        categoryKey: '',
        name: '',
        description: '',
        parentKey: '',
        sortOrder: 1,
        isActive: true
      },
      defaultItem: {
        categoryKey: '',
        name: '',
        description: '',
        parentKey: null,
        sortOrder: 1,
        isActive: true
      }
    }
  },

  computed: {
    categories() {
      return this.$store.state.categories.all || [];
    },

    categoryTree() {
      return this.$store.state.categories.tree || [];
    },

    parentOptions() {
      // Aktif kategorileri al
      let availableCategories = this.categories.filter(c => c.isActive);
      
      // Düzenleme modundaysa, kendisi ve alt kategorilerini çıkar
      if (this.editedIndex !== -1) {
        const editedCategoryKey = this.editedItem.categoryKey;
        availableCategories = availableCategories.filter(c => {
          return c.categoryKey !== editedCategoryKey && !this.isDescendant(c.categoryKey, editedCategoryKey);
        });
      }
      
      // Kategorileri levelDepth ve sortOrder'a göre sırala
      const sortedCategories = [...availableCategories].sort((a, b) => {
        if (a.levelDepth !== b.levelDepth) return a.levelDepth - b.levelDepth;
        return a.sortOrder - b.sortOrder;
      });
      
      // Ağaç yapısında listeyi oluştur
      const treeOptions = [];
      
      const buildTreeOptions = (parentKey = null, prefix = "") => {
        const children = sortedCategories.filter(c => {
          const catParentKey = c.parentKey || c.ParentKey;
          if (parentKey === null) {
            return !catParentKey || catParentKey === "";
          }
          return catParentKey === parentKey;
        });
        
        children.forEach(category => {
          const catKey = category.categoryKey || category.CategoryKey;
          const catName = category.name || category.Name || catKey;
          if (catKey) {
            treeOptions.push({
              ...category,
              name: prefix + catName,
              categoryKey: catKey
            });
            // Alt kategorileri ekle
            buildTreeOptions(catKey, prefix + "— ");
          }
        });
      };
      
      buildTreeOptions(null, "");
      
      return treeOptions;
    },

    isFormValid() {
      return this.editedItem.categoryKey && 
             this.editedItem.name && 
             this.keyErrors.length === 0;
    }
  },

  async mounted() {
    const savedTenantId = localStorage.getItem('selectedTenantId');
    if (savedTenantId && savedTenantId !== '0') {
      this.selectedTenantId = parseInt(savedTenantId);
      await this.initialize();
    }
  },

  methods: {
    async onTenantChange() {
      if (this.selectedTenantId) {
        localStorage.setItem('selectedTenantId', this.selectedTenantId.toString());
        localStorage.removeItem('selectedMpId');
        
        this.$store.commit("setCategories", []);
        this.$store.commit("setCategoryTree", []);
        await this.initialize();
      } else {
        this.$store.commit("setCategories", []);
        this.$store.commit("setCategoryTree", []);
      }
    },

    async initialize() {
      if (!this.selectedTenantId) {
        return; 
      }
      
      this.loading = true;
      try {
        await this.$store.dispatch('getAllCategories');
        await this.$store.dispatch('getCategoryTree');
      } catch (error) {
        this.showError('Kategoriler yüklenirken hata oluştu');
      } finally {
        this.loading = false;
      }
    },

    async showAddDialog() {
      if (!this.selectedTenantId) {
        this.showError('Lütfen önce bir Tenant seçiniz!');
        return;
      }
      
      this.editedIndex = -1;
      this.editedItem = { ...this.defaultItem };
      await this.setDefaultSortOrder();
      this.dialog = true;
    },

    editCategory(item) {
      this.editedIndex = this.categories.indexOf(item);
      this.editedItem = { ...item };
      this.dialog = true;
    },

    editCategoryFromTree(item) {
      const category = this.categories.find(c => c.id === item.id);
      if (category) {
        this.editCategory(category);
      }
    },

    deleteCategory(item) {
      this.categoryToDelete = item;
      this.deleteDialog = true;
    },

    deleteCategoryFromTree(item) {
      const category = this.categories.find(c => c.id === item.id);
      if (category) {
        this.deleteCategory(category);
      }
    },

    async confirmDelete() {
      this.deleting = true;
      try {
        await this.$store.dispatch('deleteCategory', this.categoryToDelete.id);
        this.showSuccess('Kategori başarıyla silindi');
        this.deleteDialog = false;
        this.categoryToDelete = null;
      } catch (error) {
        const errorMessage = error.response && error.response.data && error.response.data.message 
          ? error.response.data.message 
          : 'Kategori silinirken hata oluştu';
        this.showError(errorMessage);
      } finally {
        this.deleting = false;
      }
    },

    async save() {
      if (!this.isFormValid) return;

      this.saving = true;
      try {
        if (this.editedIndex === -1) {
          await this.$store.dispatch('createCategory', this.editedItem);
          this.showSuccess('Kategori başarıyla oluşturuldu');
        } else {
          await this.$store.dispatch('updateCategory', {
            id: this.editedItem.id,
            category: this.editedItem
          });
          this.showSuccess('Kategori başarıyla güncellendi');
        }
        this.close();
        
        await this.$store.dispatch('getCategoryTree');
      } catch (error) {
        const errorMessage = error.response && error.response.data && error.response.data.message 
          ? error.response.data.message 
          : 'Kategori kaydedilirken hata oluştu';
        this.showError(errorMessage);
      } finally {
        this.saving = false;
      }
    },

    close() {
      this.dialog = false;
      this.editedItem = { ...this.defaultItem };
      this.editedIndex = -1;
      this.keyErrors = [];
    },

    sanitizeCategoryKey() {
      if (this.editedItem.categoryKey) {
        const sanitized = this.editedItem.categoryKey.replace(/[^a-zA-Z0-9\-_]/g, '');
        if (sanitized !== this.editedItem.categoryKey) {
          this.editedItem.categoryKey = sanitized;
        }
      }
    },

    async checkCategoryKey() {
      if (!this.editedItem.categoryKey) {
        this.keyErrors = [];
        return;
      }

      try {
        const excludeId = this.editedIndex !== -1 ? this.editedItem.id : null;
        const exists = await this.$store.dispatch('checkCategoryKey', {
          categoryKey: this.editedItem.categoryKey,
          excludeId
        });

        this.keyErrors = exists ? ['Bu kategori anahtarı zaten kullanılıyor'] : [];
      // eslint-disable-next-line no-empty
      } catch (error) {}
    },

    async onParentChange() {
      await this.setDefaultSortOrder();
    },

    async setDefaultSortOrder() {
      try {
        const maxSortOrder = await this.$store.dispatch('getMaxSortOrder', this.editedItem.parentKey);
        if (this.editedIndex === -1) {
          this.editedItem.sortOrder = maxSortOrder;
        }
      // eslint-disable-next-line no-empty
      } catch (error) {}
    },

    isDescendant(categoryKey, ancestorKey) {
      const category = this.categories.find(c => c.categoryKey === categoryKey);
      if (!category || !category.parentKey) return false;
      
      if (category.parentKey === ancestorKey) return true;
      
      return this.isDescendant(category.parentKey, ancestorKey);
    },

    showSuccess(message) {
      this.snackbarText = message;
      this.snackbarColor = 'success';
      this.snackbar = true;
    },

    showError(message) {
      this.snackbarText = message;
      this.snackbarColor = 'error';
      this.snackbar = true;
    }
  },

  beforeDestroy() {
    localStorage.setItem('selectedTenantId', '0');
    localStorage.removeItem('selectedMpId');
    this.$store.commit("setCategories", []);
    this.$store.commit("setCategoryTree", []);
  }
}
</script>

<style scoped>
.category-tree-container {
  margin: 16px;
  border-radius: 10px;
  overflow: hidden;
  background-color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.enhanced-tree {
  padding: 12px 0;
}

/* Tree düğümleri */
.enhanced-tree >>> .v-treeview-node__root {
  padding: 12px 16px;
  border-radius: 8px;
  margin: 6px 12px;
  transition: all 0.2s ease;
  background-color: #fafafa;
  display: flex;
  align-items: center;
}

.enhanced-tree >>> .v-treeview-node__root:hover {
  background-color: rgba(33, 150, 243, 0.08);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

/* Alt seviye kategoriler */
.enhanced-tree >>> .v-treeview-node__children .v-treeview-node__root {
  background-color: #f4f4f4;
}

.enhanced-tree >>> .v-treeview-node__children .v-treeview-node__root:hover {
  background-color: rgba(33, 150, 243, 0.12);
}

.tree-item-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 10px;
  gap: 10px;
}

.item-row-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.item-row-bottom {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.category-name {
  font-size: 15px;
  font-weight: 600;
  color: #212121;
  letter-spacing: 0.2px;
  flex: 1;
}

.tree-item-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* İkon butonlar */
.action-btn {
  background-color: transparent !important;
  box-shadow: none !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  padding: 6px !important;
  border-radius: 6px !important;
  min-width: 32px !important;
  min-height: 32px !important;
  transition: background-color 0.15s ease, transform 0.15s ease;
}

.action-btn i,
.action-btn .v-icon {
  display: block !important;
  margin: 0 !important;
  line-height: 1 !important;
  font-size: 18px !important;
}

.action-btn:hover {
  background-color: rgba(0, 0, 0, 0.06) !important;
  transform: scale(1.1);
}

.action-btn:before {
  background-color: transparent !important;
}

/* Açılır klasör ikonu dönüş efekti */
.rotate-icon {
  transform: rotate(0deg);
  transition: transform 0.2s ease;
}

.v-treeview-node--open .rotate-icon {
  transform: rotate(90deg);
}

.mobile-toolbar {
  padding: 0 16px;
}

/* Responsive ayarlar */
@media (max-width: 600px) {
  .enhanced-tree >>> .v-treeview-node__root {
    padding: 8px 12px;
    margin: 5px 8px;
  }
  .item-row-top {
    flex-wrap: wrap;
    gap: 8px;
  }
  .tree-item-actions {
    width: 100%;
    justify-content: flex-end;
  }
  .item-row-bottom {
    width: 100%;
  }
}

/* Boş ve yükleniyor durumları */
.text-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.text-center p {
  font-size: 14px;
  color: #555;
}

.v-alert {
  border-radius: 8px;
}

.v-toolbar-title {
  font-weight: 600;
  color: #1a1a1a;
}
</style>